// Copyright ©️ 2026 eVolve MEP, LLC

import { AutodeskAuthWrapper } from 'components/Autodesk/auth/AutodeskAuthWrapper';
import { WrappedSelect } from 'components/Mantine/TypeSafeSelect/WrappedSelect';

import type { useModelSelection } from './useModelSelection';

type Props = ReturnType<typeof useModelSelection>['modelSelectProps'];

/**
 * Dropdown of the Autodesk models available on a project.
 *
 * Pair it with `useModelSelection`, which owns the data and the selection.
 * Spread the hook's `modelSelectProps` into this component's props.
 * @example
 * ```tsx
 * const { modelSelectProps } = useModelSelection(projectId);
 * <ModelSelect {...modelSelectProps} />
 * ```
 */
export const ModelSelect = ({ models, modelCache, loading, value, onChange }: Props) => (
  <AutodeskAuthWrapper>
    <WrappedSelect
      value={value}
      data={
        models?.map((m) => ({
          value: m.urn,
          label: modelCache[m.urn]?.attributes.displayName ?? m.urn,
        })) ?? []
      }
      searchable
      w={400}
      onChange={onChange}
      loading={loading}
      alwaysShowWhenLoading
      placeholder="Select a model..."
      nothingFoundMessage="No models linked to selected project."
      autoDropdownWidth
    />
  </AutodeskAuthWrapper>
);
